import React, {useMemo} from 'react';
import {HStack, VStack} from 'native-base';
import {useSortable} from '@dnd-kit/sortable';
import {Tooltip} from 'antd';
import AppText from '../../../../../common/AppText';
import {GlobalStyleAttributes} from '../../../../../../styles/GlobalStyles';
import {t} from '../../../../../../services/i18n';
import SimpleInput from '../../../../../common/SimpleInput';
import MultiSingleMenuWithSearch from '../../../../../common/MultiSingleMenuWithSearch';
import CustomButtonV2 from '../../../../../common/CustomButtonV2';
import {GlobalConsts} from '../../../../../../styles/GlobalConsts';
import {Colors} from '../../../../../../styles/Colors';

const UpsellRow = (props) => {
    const {row, index, productOptions, currencySymbol, onProductChange, onPriceChange, onDelete, error} = props;
    const {transform, transition, isDragging, setNodeRef, listeners} = useSortable({id: row.randomKey});

    const commonStyle = {marginTop: index === 0 ? 0 : '10px', transition: 'unset'};
    const style = transform ? {...commonStyle, transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, transition: isDragging ? 'unset' : transition} : commonStyle;

    // Preview helper: only when product + New Price are set and in range
    const helperText = useMemo(() => {
        const hasProduct = row.membership_types_id != null;
        const hasPrice = row.price !== null && row.price !== '' && row.price !== undefined;
        if (!hasProduct || !hasPrice || row.originalPrice == null) return null;

        const originalPrice = Number(row.originalPrice);
        const newPrice = Number(row.price);
        if (Number.isNaN(originalPrice) || Number.isNaN(newPrice) || newPrice < 0 || newPrice > originalPrice) return null;

        const effectivePrice = Math.min(newPrice, originalPrice);
        const discountPercentage = originalPrice === 0 ? 0 : Math.round(((originalPrice - effectivePrice) / originalPrice) * 100);
        return t('upsell-helper-discount', {discountPercentage, effectivePrice: `${effectivePrice.toLocaleString()}${currencySymbol ?? ''}`});
    }, [row.membership_types_id, row.price, row.originalPrice, currencySymbol]);

    return (
        <div style={style} ref={setNodeRef}>
            <VStack space={'0.2rem'} flex={1}>
                <HStack space={'1rem'} alignItems={'flex-start'}>
                    <VStack space={'0.2rem'} flex={1}>
                        <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('upsell-item-label', {n: index + 1})}</AppText>
                        <MultiSingleMenuWithSearch options={productOptions} labelProp={'label'} notMultiple allowClear
                                                   placeholder={t('upsell-product-select-placeholder')}
                                                   value={row.membership_types_id ?? undefined}
                                                   onChange={(val) => onProductChange(index, val ?? null)}
                                                   searchable={true}
                                                   status={error ? 'error' : null}/>
                    </VStack>
                    <VStack space={'0.2rem'}>
                        <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('upsell-new-price-label')}</AppText>
                        <HStack space={'1rem'} alignItems={'center'}>
                            <SimpleInput value={row.price} inputType={'float'} formatAddon={currencySymbol}
                                         onChangeCallback={(val) => onPriceChange(index, val)} width={'6rem'}
                                         styleOverride={{height: '38px', display: 'flex', alignItems: 'center'}}
                                         styles={{input: {height: '38px', lineHeight: '38px'}}}
                                         status={error ? 'error' : null}/>
                            <CustomButtonV2 onClick={() => onDelete(index)} iconName={'fa-trash'} type={'link'}/>
                            <Tooltip title={t('upsell-drag-tooltip')}>
                                <span>
                                    <CustomButtonV2 iconName={'fa-grip-dots-vertical'} type={'link'} style={{cursor: 'move'}}
                                                    iconProps={{iconPrefix: GlobalConsts.iconPrefix.SOLID, iconHoverColor: 'unset'}} {...listeners}/>
                                </span>
                            </Tooltip>
                        </HStack>
                    </VStack>
                </HStack>
                {error && <AppText fontSize={GlobalStyleAttributes.fonts.infoText} color={Colors.error} mediumFont>{error}</AppText>}
                {helperText && !error && <AppText fontSize={GlobalStyleAttributes.fonts.infoText} color={'#757575'}>{helperText}</AppText>}
            </VStack>
        </div>
    );
};

export default UpsellRow;
